import React, { useEffect, useState, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { AnimatePresence } from "framer-motion";
import { Building2 } from "lucide-react";
import InspectionToast from "./inspections/InspectionToast";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useWorkshop } from "../contexts/WorkshopContext";
import { BoardHeader } from "./InspectionBoard/components/BoardHeader";
import WorkshopSelector from "./WorkshopSelector";
import { LoadingState } from "./InspectionBoard/components/LoadingState";
import { EmptyState } from "./InspectionBoard/components/EmptyState";
import { InspectionCard } from "./InspectionBoard/components/InspectionCard";
import { DateRangeReportModal } from "./InspectionBoard/modals/DateRangeReportModal";
import { ConfirmCloseModal } from "./InspectionBoard/modals/ConfirmCloseModal";
import { CreateInspectionModal } from "./InspectionBoard/modals/CreateInspectionModal";
import { DetailInspectionModal } from "./InspectionBoard/modals/DetailInspectionModal";
import { ReportPreviewModal } from "./InspectionBoard/modals/ReportPreviewModal";
import type {
  Vehicle,
  ActiveInspection,
  SheetDetail,
  ItemDetail,
  InspectionGridItem,
} from "./InspectionBoard/types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:8000";

const TAB_AUTO_SWITCH_INTERVAL = 5 * 60 * 1000; // 5 minutos
const AUTO_SCROLL_SPEED = 30; // pixels per second

const InspectionBoard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { isDarkMode } = useTheme();
  const { selectedWorkshop, workshops } = useWorkshop();
  const [gridItems, setGridItems] = useState<InspectionGridItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollingTimeouts = useRef<(NodeJS.Timeout | null)[]>([]);
  const gridItemsRef = useRef<InspectionGridItem[]>([]);
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: "added" | "removed" | "updated";
  }>({
    show: false,
    message: "",
    type: "added",
  });

  // States for create inspection modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [driverName, setDriverName] = useState("");
  const [mechanicName, setMechanicName] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // States for detail/edit inspection modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInspection, setSelectedInspection] =
    useState<InspectionGridItem | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [savingResponse, setSavingResponse] = useState<number | null>(null);
  const [confirmCloseId, setConfirmCloseId] = useState<number | null>(null);
  const [closeComment, setCloseComment] = useState("");
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [selectedReportInspection, setSelectedReportInspection] = useState<{
    id: number;
    vehicleNumber: string;
  } | null>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const showToast = (
    message: string,
    type: "added" | "removed" | "updated"
  ) => {
    setToast({ show: true, message, type });
  };

  // Log mount/unmount
  useEffect(() => {
    return () => {};
  }, []);

  // Keep ref synchronized with state
  useEffect(() => {
    gridItemsRef.current = gridItems;
  }, [gridItems]);

  const loadInspections = async () => {
    if (!selectedWorkshop) {
      setGridItems([]);
      return;
    }

    try {
      // Clear existing inspections immediately when loading for a new workshop
      // Don't clear gridItems here to preserve modal state
      // Only clear if gridItems is empty (first load or workshop change)

      const response = await fetch(
        `${API_BASE_URL}/inspections/active?workshop_id=${selectedWorkshop.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const data = await response.json();

      if (Array.isArray(data)) {
        // Create or update grid items using ref - ALL active inspections
        const newGridItems = await Promise.all(
          data.map(async (inspection: ActiveInspection) => {
            // Check if this item already exists using ref
            const existingItem = gridItemsRef.current.find(
              (item) => item.inspection.id === inspection.id
            );

            if (existingItem) {
              // Keep existing state
              return { ...existingItem, inspection };
            } else {
              // New item, load details
              const sheetDetails = await loadInspectionDetails(inspection.id);
              return {
                inspection,
                activeTab: 0,
                sheetDetails,
                lastInteraction: new Date(),
                scrollPosition: 0,
                isHovered: false,
                isScrollingToTop: false,
                timeUntilTabSwitch: TAB_AUTO_SWITCH_INTERVAL / 1000,
              };
            }
          })
        );

        setGridItems(newGridItems);
      } else {
        setGridItems([]);
      }
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error al cargar inspecciones:", error);
      setGridItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInspectionDetails = async (
    inspectionId: number
  ): Promise<SheetDetail[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/inspections/${inspectionId}`
      );

      if (!response.ok) {
        console.error(
          `❌ Error loading inspection ${inspectionId}:`,
          response.status
        );
        return [];
      }

      const data = await response.json();

      if (data.sheets) {
        const sheetsWithItems = await Promise.all(
          data.sheets.map(async (sheet: any) => {
            try {
              const itemsResponse = await fetch(
                `${API_BASE_URL}/inspections/templates/${sheet.template_id}/items`
              );
              const items = await itemsResponse.json();

              const responsesResponse = await fetch(
                `${API_BASE_URL}/inspections/sheets/${sheet.id}/responses`
              );
              const responsesData = await responsesResponse.json();

              const itemsWithResponses = items.map((item: any) => {
                const responseObj = responsesData.responses?.find(
                  (r: any) => r.item_id === item.id
                );
                return {
                  ...item,
                  response: responseObj?.response,
                  notes: responseObj?.notes || "",
                };
              });

              return {
                ...sheet,
                items: itemsWithResponses,
              };
            } catch (error) {
              console.error("Error loading sheet details:", error);
              return {
                ...sheet,
                items: [],
              };
            }
          })
        );

        return sheetsWithItems;
      }
      return [];
    } catch (error) {
      console.error(`❌ Error loading details for ${inspectionId}:`, error);
      return [];
    }
  };

  // Search vehicles
  const searchVehicles = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    if (!selectedWorkshop) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/external-cars?search=${encodeURIComponent(
          query
        )}&limit=10&workshop_id=${selectedWorkshop.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        // Filter out vehicles that already have active inspections
        const activeVehicleIds = new Set(
          gridItems.map((item) => item.inspection.vehicle_id)
        );
        const availableVehicles = data.data.filter(
          (vehicle: Vehicle) => !activeVehicleIds.has(vehicle.id)
        );
        setSearchResults(availableVehicles);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching vehicles:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Create new inspection
  const createInspection = async () => {
    if (!selectedVehicle) {
      showToast("Please select a vehicle", "removed");
      return;
    }

    if (!selectedWorkshop) {
      showToast("Please select a workshop first", "removed");
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/inspections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({
          vehicle_id: selectedVehicle.id,
          workshop_id: selectedWorkshop.id,
          created_by: user?.username || "unknown",
          driver_name: driverName || null,
          mechanic_name: mechanicName || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showToast(`Inspection #${data.id} created successfully`, "added");

        // Close modal and clean up
        setShowCreateModal(false);
        setSelectedVehicle(null);
        setVehicleSearch("");
        setDriverName("");
        setMechanicName("");
        setSearchResults([]);

        // Reload inspections
        await loadInspections();
      } else {
        const error = await response.json();
        showToast(
          `Error: ${error.detail || "Could not create inspection"}`,
          "removed"
        );
      }
    } catch (error) {
      console.error("Error creating inspection:", error);
      showToast("Error creating inspection", "removed");
    } finally {
      setIsCreating(false);
    }
  };

  // Toggle inspection status
  const toggleInspectionStatus = async (
    inspectionId: number,
    currentStatus: string,
    comment?: string
  ) => {
    const newStatus = currentStatus === "open" ? "closed" : "open";
    setUpdatingStatus(true);

    try {
      const body: { status: string; comment?: string } = { status: newStatus };
      if (comment && newStatus === "closed") {
        body.comment = comment;
      }

      const response = await fetch(
        `${API_BASE_URL}/inspections/${inspectionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        showToast(
          `Inspection ${
            newStatus === "closed" ? "closed" : "reopened"
          } successfully`,
          "updated"
        );
        await loadInspections();
      } else {
        const error = await response.json();
        showToast(
          `Error: ${error.detail || "Could not update status"}`,
          "removed"
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", "removed");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Save item response
  const saveItemResponse = async (
    sheetId: number,
    itemId: number,
    response: string | null,
    notes?: string
  ) => {
    setSavingResponse(itemId);

    try {
      const requestBody = response
        ? { response, notes: notes || "" }
        : { response: "" };

      const apiResponse = await fetch(
        `${API_BASE_URL}/inspections/sheets/${sheetId}/items/${itemId}/response`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (apiResponse.ok) {
        // Actualizar selectedInspection localmente SOLO con las notas para evitar re-render completo
        if (selectedInspection) {
          setSelectedInspection((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              sheetDetails: prev.sheetDetails.map((sheet) => {
                if (sheet.id === sheetId) {
                  return {
                    ...sheet,
                    items: sheet.items.map((item) => {
                      if (item.id === itemId) {
                        return {
                          ...item,
                          response: response || item.response,
                          notes: notes || "",
                        };
                      }
                      return item;
                    }),
                  };
                }
                return sheet;
              }),
            };
          });
        }

        // Don't reload inspections here - socket.io will handle the update
        // and reloading causes modal to close
      }
    } catch (error) {
      console.error("Error saving response:", error);
    } finally {
      setSavingResponse(null);
    }
  };

  // Open detail modal
  const openDetailModal = (gridItem: InspectionGridItem) => {
    setSelectedInspection(gridItem);
    setShowDetailModal(true);
  };

  // Debounce for vehicle search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (vehicleSearch) {
        searchVehicles(vehicleSearch);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [vehicleSearch]);

  // Auto-rotation of tabs with countdown
  useEffect(() => {
    const updateCountdown = () => {
      setGridItems((prevItems) =>
        prevItems.map((item) => {
          const timeSinceLastInteraction =
            Date.now() - item.lastInteraction.getTime();
          const timeRemaining =
            TAB_AUTO_SWITCH_INTERVAL - timeSinceLastInteraction;
          const secondsRemaining = Math.max(0, Math.ceil(timeRemaining / 1000));

          // If it's time to change tab
          if (
            timeSinceLastInteraction >= TAB_AUTO_SWITCH_INTERVAL &&
            item.sheetDetails.length > 1
          ) {
            return {
              ...item,
              activeTab: (item.activeTab + 1) % item.sheetDetails.length,
              lastInteraction: new Date(), // Reset timer
              timeUntilTabSwitch: TAB_AUTO_SWITCH_INTERVAL / 1000,
            };
          }

          return {
            ...item,
            timeUntilTabSwitch: secondsRemaining,
          };
        })
      );
    };

    // Update every second for countdown
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleTabChange = (gridIndex: number, tabIndex: number) => {
    setGridItems((prevItems) =>
      prevItems.map((item, idx) => {
        if (idx === gridIndex) {
          return {
            ...item,
            activeTab: tabIndex,
            lastInteraction: new Date(),
            scrollPosition: 0, // Reset scroll when changing tabs
            timeUntilTabSwitch: TAB_AUTO_SWITCH_INTERVAL / 1000, // Reset countdown
          };
        }
        return item;
      })
    );
  };

  // Socket.IO
  useEffect(() => {
    if (!selectedWorkshop) return;

    const newSocket = io(SOCKET_URL, {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      autoConnect: true,
      upgrade: true,
      rememberUpgrade: true,
      forceNew: false,
      multiplex: true,
    });

    newSocket.on("connect", () => {
      // Conexión exitosa
    });

    newSocket.on("connect_error", (error) => {
      // Silenciar errores de conexión en consola
      // pero el cliente seguirá intentando reconectar
    });

    newSocket.on("error", (error) => {
      // Silenciar errores de socket en consola
    });

    newSocket.on("disconnect", (reason) => {
      // Cliente desconectado, Socket.IO intentará reconectar automáticamente
    });

    newSocket.on(
      "inspection_updated",
      async (payload: { action: string; inspection_id?: number }) => {
        if (payload.inspection_id) {
          try {
            // Obtener datos actualizados de esta inspección
            const inspectionResponse = await fetch(
              `${API_BASE_URL}/inspections/${payload.inspection_id}`
            );

            // If inspection doesn't exist (404), it means it was deleted
            if (inspectionResponse.status === 404) {
              const currentItem = gridItemsRef.current.find(
                (item) => item.inspection.id === payload.inspection_id
              );

              if (currentItem) {
                setGridItems((prevItems) =>
                  prevItems.filter(
                    (item) => item.inspection.id !== payload.inspection_id
                  )
                );
                showToast(
                  `Inspection #${payload.inspection_id} deleted`,
                  "removed"
                );
              }
              return;
            }

            const inspectionData = await inspectionResponse.json();

            // Check if this inspection is on the board using current ref
            const currentItem = gridItemsRef.current.find(
              (item) => item.inspection.id === payload.inspection_id
            );

            // If inspection is closed
            if (inspectionData.status === "closed") {
              if (currentItem) {
                // Only show toast if inspection was on the board
                setGridItems((prevItems) =>
                  prevItems.filter(
                    (item) => item.inspection.id !== payload.inspection_id
                  )
                );
                showToast(
                  `Inspection #${payload.inspection_id} closed`,
                  "removed"
                );
              }
              return;
            }

            // If inspection exists on board, update it
            if (currentItem) {
              // Load sheet details with items
              const sheetsWithItems = await Promise.all(
                inspectionData.sheets.map(async (sheet: any) => {
                  try {
                    const [itemsResponse, responsesResponse] =
                      await Promise.all([
                        fetch(
                          `${API_BASE_URL}/inspections/templates/${sheet.template_id}/items`
                        ),
                        fetch(
                          `${API_BASE_URL}/inspections/sheets/${sheet.id}/responses`
                        ),
                      ]);

                    const items = await itemsResponse.json();
                    const responsesData = await responsesResponse.json();

                    // Validate that items is an array
                    if (!Array.isArray(items)) {
                      console.error(
                        `❌ Sheet ${sheet.id} - Items is NOT an array:`,
                        typeof items
                      );
                      return {
                        ...sheet,
                        items: [],
                      };
                    }

                    const itemsWithResponses = items.map((item: any) => {
                      const responseObj = responsesData.responses?.find(
                        (r: any) => r.item_id === item.id
                      );
                      return {
                        ...item,
                        response: responseObj?.response,
                        notes: responseObj?.notes || "",
                      };
                    });

                    return {
                      ...sheet,
                      items: itemsWithResponses,
                    };
                  } catch (error) {
                    console.error(`❌ Error loading sheet ${sheet.id}:`, error);
                    return {
                      ...sheet,
                      items: [],
                    };
                  }
                })
              );

              // Use progress_percentage calculated by backend
              const progressPercentage =
                inspectionData.progress_percentage || 0;
              const totalSheets =
                inspectionData.total_sheets || sheetsWithItems.length;
              const completedSheets =
                inspectionData.completed_sheets ||
                sheetsWithItems.filter((s) => s.status === "completed").length;

              setGridItems((prevItems) =>
                prevItems.map((item) => {
                  if (item.inspection.id === payload.inspection_id) {
                    return {
                      ...item,
                      inspection: {
                        ...item.inspection,
                        status: inspectionData.status,
                        updated_at:
                          inspectionData.updated_at ||
                          item.inspection.updated_at,
                        total_sheets: totalSheets,
                        completed_sheets: completedSheets,
                        progress_percentage: progressPercentage,
                      },
                      sheetDetails: sheetsWithItems,
                    };
                  }
                  return item;
                })
              );

              // También actualizar selectedInspection si el modal está abierto para esa inspección
              setSelectedInspection((prevSelected) => {
                if (
                  prevSelected &&
                  prevSelected.inspection.id === payload.inspection_id
                ) {
                  return {
                    ...prevSelected,
                    inspection: {
                      ...prevSelected.inspection,
                      status: inspectionData.status,
                      updated_at:
                        inspectionData.updated_at ||
                        prevSelected.inspection.updated_at,
                      total_sheets: totalSheets,
                      completed_sheets: completedSheets,
                      progress_percentage: progressPercentage,
                    },
                    sheetDetails: sheetsWithItems,
                  };
                }
                return prevSelected;
              });

              showToast(
                `Inspection #${payload.inspection_id} updated`,
                "updated"
              );
            } else {
              // New inspection not on board - add it without reloading everything
              // Load sheet details with items for the new inspection
              const sheetsWithItems = await Promise.all(
                inspectionData.sheets.map(async (sheet: any) => {
                  try {
                    const [itemsResponse, responsesResponse] =
                      await Promise.all([
                        fetch(
                          `${API_BASE_URL}/inspections/templates/${sheet.template_id}/items`
                        ),
                        fetch(
                          `${API_BASE_URL}/inspections/sheets/${sheet.id}/responses`
                        ),
                      ]);
                    const items = await itemsResponse.json();
                    const responsesData = await responsesResponse.json();

                    if (!Array.isArray(items)) {
                      return { ...sheet, items: [] };
                    }

                    const itemsWithResponses = items.map((item: any) => {
                      const responseObj = responsesData.responses?.find(
                        (r: any) => r.item_id === item.id
                      );
                      return {
                        ...item,
                        response: responseObj?.response,
                        notes: responseObj?.notes || "",
                      };
                    });

                    return { ...sheet, items: itemsWithResponses };
                  } catch (error) {
                    console.error(`Error loading sheet ${sheet.id}:`, error);
                    return { ...sheet, items: [] };
                  }
                })
              );

              // Add new inspection to the board
              const newItem: InspectionGridItem = {
                inspection: inspectionData,
                activeTab: 0,
                sheetDetails: sheetsWithItems,
                lastInteraction: new Date(),
                scrollPosition: 0,
                isHovered: false,
                isScrollingToTop: false,
                timeUntilTabSwitch: TAB_AUTO_SWITCH_INTERVAL / 1000,
              };

              setGridItems((prevItems) => [...prevItems, newItem]);
              showToast(
                `New inspection #${payload.inspection_id} added`,
                "added"
              );
            }
          } catch (error) {
            console.error("Error updating inspection:", error);
            // Don't reload everything on error - it causes modal loss
            // Only log the error and let the next socket event fix it
          }
        } else {
          // No inspection_id, only reload if action requires it
          if (
            payload.action === "created" ||
            payload.action === "bulk_update"
          ) {
            loadInspections();
          }
        }
      }
    );

    newSocket.on("disconnect", () => {});

    setSocket(newSocket);
    socketRef.current = newSocket;
    loadInspections();

    return () => {
      // Solo limpiar si realmente se desmonta o cambia el workshop
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [selectedWorkshop]);

  // Auto-scroll effect for each grid item independently
  useEffect(() => {
    if (gridItems.length === 0) return;

    const scrollIntervals: NodeJS.Timeout[] = [];

    // Create an interval for each inspection item
    gridItems.forEach((item, index) => {
      const inspectionId = item.inspection.id;

      const interval = setInterval(() => {
        const currentContentDiv = contentRefs.current[index];
        if (!currentContentDiv) {
          return;
        }

        const scrollHeight = currentContentDiv.scrollHeight;
        const clientHeight = currentContentDiv.clientHeight;
        const maxScroll = scrollHeight - clientHeight;

        if (maxScroll <= 0) return; // No need to scroll if content fits

        setGridItems((prevItems) => {
          // Ensure the item still exists at this index and has the same ID
          if (
            !prevItems[index] ||
            prevItems[index].inspection.id !== inspectionId
          ) {
            return prevItems;
          }

          const currentItem = prevItems[index];

          // Don't scroll if mouse is hovering or doing smooth scroll to top
          if (currentItem.isHovered || currentItem.isScrollingToTop)
            return prevItems;

          let newScrollPosition = currentItem.scrollPosition + 1;

          // When reaching the end, mark to start smooth scroll
          if (newScrollPosition >= maxScroll) {
            const newItems = [...prevItems];
            newItems[index] = {
              ...currentItem,
              scrollPosition: maxScroll,
              isScrollingToTop: true,
            };
            return newItems;
          }

          const newItems = [...prevItems];
          newItems[index] = {
            ...currentItem,
            scrollPosition: newScrollPosition,
          };
          return newItems;
        });
      }, 1000 / AUTO_SCROLL_SPEED); // Update based on scroll speed

      scrollIntervals.push(interval);
    });

    return () => {
      scrollIntervals.forEach((interval) => clearInterval(interval));
    };
  }, [gridItems.map((item) => item.inspection.id).join(",")]);

  // Handle mouse hover events
  const handleMouseEnter = (gridIndex: number) => {
    setGridItems((prevItems) =>
      prevItems.map((item, idx) => {
        if (idx === gridIndex) {
          return { ...item, isHovered: true };
        }
        return item;
      })
    );
  };

  const handleMouseLeave = (gridIndex: number) => {
    setGridItems((prevItems) =>
      prevItems.map((item, idx) => {
        if (idx === gridIndex) {
          return { ...item, isHovered: false };
        }
        return item;
      })
    );
  };
  // Apply scroll position to DOM - Use animation frame for better performance
  useEffect(() => {
    let frameId: number;

    const applyScroll = () => {
      gridItems.forEach((item, index) => {
        const contentDiv = contentRefs.current[index];
        if (contentDiv && item) {
          const scrollHeight = contentDiv.scrollHeight;
          const clientHeight = contentDiv.clientHeight;
          const maxScroll = scrollHeight - clientHeight;

          // Only update scroll if mouse is NOT hovering
          if (maxScroll > 0 && !item.isHovered) {
            // If marked for smooth scroll to top
            if (item.isScrollingToTop) {
              // Clear previous timeout if exists
              if (scrollingTimeouts.current[index]) {
                clearTimeout(scrollingTimeouts.current[index]!);
              }

              contentDiv.scrollTo({
                top: 0,
                behavior: "smooth",
              });

              // After smooth scroll, reset state only ONCE
              scrollingTimeouts.current[index] = setTimeout(() => {
                setGridItems((prevItems) => {
                  if (!prevItems[index]) return prevItems;

                  const newItems = [...prevItems];
                  newItems[index] = {
                    ...prevItems[index],
                    scrollPosition: 0,
                    isScrollingToTop: false,
                  };
                  return newItems;
                });
                scrollingTimeouts.current[index] = null;
              }, 1000); // Time to complete smooth animation
            } else {
              // Normal automatic scroll
              const targetScroll = Math.min(item.scrollPosition, maxScroll);
              contentDiv.scrollTop = targetScroll;
            }
          }
        }
      });

      frameId = requestAnimationFrame(applyScroll);
    };

    frameId = requestAnimationFrame(applyScroll);

    return () => {
      cancelAnimationFrame(frameId);
      // Clear any pending timeouts when component unmounts or gridItems change
      Object.keys(scrollingTimeouts.current).forEach((key) => {
        const index = parseInt(key);
        // Clear timeouts for indices that no longer exist
        if (index >= gridItems.length && scrollingTimeouts.current[index]) {
          clearTimeout(scrollingTimeouts.current[index]!);
          scrollingTimeouts.current[index] = null;
        }
      });
    };
  }, [gridItems]);

  // Calcular grid class según cantidad de inspecciones - DINÁMICO para cualquier cantidad
  const getGridClass = (count: number): string => {
    if (count === 0) return "grid-cols-1"; // Sin inspecciones
    if (count === 1) return "grid-cols-1 grid-rows-1 place-items-stretch"; // 1 = usa todo el espacio
    if (count === 2) return "grid-cols-2 grid-rows-1 place-items-stretch"; // 2 = 2 columnas
    if (count === 3) return "grid-cols-3 grid-rows-1 place-items-stretch"; // 3 = 3 columnas
    if (count === 4) return "grid-cols-2 grid-rows-2 place-items-stretch"; // 4 = 2x2
    if (count === 5 || count === 6)
      return "grid-cols-3 grid-rows-2 place-items-stretch"; // 5-6 = 3x2
    if (count <= 9) return "grid-cols-3 grid-rows-3 place-items-stretch"; // 7-9 = 3x3
    if (count <= 12) return "grid-cols-4 grid-rows-3 place-items-stretch"; // 10-12 = 4x3
    if (count <= 16) return "grid-cols-4 grid-rows-4 place-items-stretch"; // 13-16 = 4x4
    return "grid-cols-5 grid-rows-4 place-items-stretch"; // 17+ = 5x4
  };

  const getItemClass = (count: number): string => {
    // Usa todo el espacio disponible en el grid - Responsivo para pantallas grandes
    // Solo altura máxima para mantener proporción
    if (count === 1) return "w-full h-full max-h-[900px]";
    if (count === 2) return "w-full h-full max-h-[850px]";
    if (count === 3) return "w-full h-full max-h-[850px]";
    if (count === 4) return "w-full h-full max-h-[700px]";
    if (count <= 6) return "w-full h-full max-h-[700px]"; // 5-6
    if (count <= 9) return "w-full h-full max-h-[550px]"; // 7-9
    if (count <= 12) return "w-full h-full max-h-[500px]"; // 10-12
    if (count <= 16) return "w-full h-full max-h-[450px]"; // 13-16
    return "w-full h-full max-h-[400px]"; // 17+
  };

  const getTabTitle = (type: string): string => {
    const titles: { [key: string]: string } = {
      PRETRIP: "PRETRIP",
      "DOT-Tractor": "DOT Tractor",
      "DOT-Trailer": "DOT Trailer",
    };
    // Replace underscores with spaces
    return (titles[type] || type).replace(/_/g, " ");
  };

  const getResponseColor = (response?: string): string => {
    if (!response) return "bg-gray-200 text-gray-700";
    if (response === "pass") return "bg-green-500 text-white";
    if (response === "fail") return "bg-red-500 text-white";
    if (response === "na") return "bg-gray-400 text-white";
    if (response === "checked") return "bg-blue-500 text-white";
    return "bg-gray-200 text-gray-700";
  };

  const getResponseText = (response?: string): string => {
    if (!response) return "No Response";
    if (response === "pass") return "Pass";
    if (response === "fail") return "Fail";
    if (response === "na") return "N/A";
    if (response === "checked") return "✓";
    return response;
  };

  if (loading) {
    return <LoadingState isDarkMode={isDarkMode} />;
  }

  // Show empty state if no workshop is selected
  if (!selectedWorkshop) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <div
          className={`text-center p-8 rounded-lg ${
            isDarkMode ? "bg-gray-800 text-gray-300" : "bg-white text-gray-600"
          }`}
        >
          <div className="text-6xl mb-4">🏢</div>
          <h2
            className={`text-2xl font-bold mb-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            No Workshop Selected
          </h2>
          <p>Please select a workshop to view inspections</p>
        </div>
      </div>
    );
  }

  if (gridItems.length === 0) {
    return (
      <>
        <EmptyState
          isDarkMode={isDarkMode}
          onCreateClick={() => setShowCreateModal(true)}
        />
        <CreateInspectionModal
          show={showCreateModal}
          isDarkMode={isDarkMode}
          vehicleSearch={vehicleSearch}
          searchResults={searchResults}
          selectedVehicle={selectedVehicle}
          driverName={driverName}
          mechanicName={mechanicName}
          isSearching={isSearching}
          isCreating={isCreating}
          onVehicleSearchChange={(value) => {
            setVehicleSearch(value);
            setSelectedVehicle(null);
          }}
          onVehicleSelect={(vehicle) => {
            setSelectedVehicle(vehicle);
            setVehicleSearch("");
            setSearchResults([]);
            if (vehicle?.driver) {
              setDriverName(vehicle.driver);
            }
          }}
          onDriverNameChange={setDriverName}
          onMechanicNameChange={setMechanicName}
          onClose={() => setShowCreateModal(false)}
          onCreate={createInspection}
        />
      </>
    );
  }

  // Conditional workshop control component
  const workshopControl = isAdmin ? (
    <WorkshopSelector />
  ) : selectedWorkshop ? (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white">
      <Building2 className="w-4 h-4" />
      <span className="text-sm font-medium">{selectedWorkshop.name}</span>
    </div>
  ) : null;

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}
    >
      <BoardHeader
        isDarkMode={isDarkMode}
        lastUpdate={lastUpdate}
        totalInspections={gridItems.length}
        isConnected={socket?.connected || false}
        onCreateClick={() => setShowCreateModal(true)}
        onReportsClick={() => setShowDateRangeModal(true)}
        workshopSelector={workshopControl}
      />

      {/* Grid de inspecciones */}
      <div
        className={`grid ${getGridClass(
          gridItems.length
        )} gap-2 p-2 h-[calc(100vh-56px)]`}
      >
        <AnimatePresence mode="popLayout">
          {gridItems.map((gridItem, gridIndex) => (
            <InspectionCard
              key={gridItem.inspection.id}
              gridItem={gridItem}
              gridIndex={gridIndex}
              gridItemsLength={gridItems.length}
              isDarkMode={isDarkMode}
              updatingStatus={updatingStatus}
              contentRef={(el) => (contentRefs.current[gridIndex] = el)}
              onMouseEnter={() => handleMouseEnter(gridIndex)}
              onMouseLeave={() => handleMouseLeave(gridIndex)}
              onTabChange={(tabIndex) => handleTabChange(gridIndex, tabIndex)}
              onScroll={(e) => {
                if (gridItems[gridIndex]?.isHovered) {
                  const target = e.target as HTMLDivElement;
                  setGridItems((prevItems) =>
                    prevItems.map((item, idx) =>
                      idx === gridIndex
                        ? { ...item, scrollPosition: target.scrollTop }
                        : item
                    )
                  );
                }
              }}
              onOpenDetail={() => openDetailModal(gridItem)}
              onDownloadReport={() => {
                setSelectedReportInspection({
                  id: gridItem.inspection.id,
                  vehicleNumber: gridItem.inspection.vehicle_number,
                });
                setShowReportPreview(true);
              }}
              onConfirmClose={() => setConfirmCloseId(gridItem.inspection.id)}
            />
          ))}
        </AnimatePresence>
      </div>

      <CreateInspectionModal
        show={showCreateModal}
        isDarkMode={isDarkMode}
        vehicleSearch={vehicleSearch}
        searchResults={searchResults}
        selectedVehicle={selectedVehicle}
        driverName={driverName}
        mechanicName={mechanicName}
        isSearching={isSearching}
        isCreating={isCreating}
        onVehicleSearchChange={(value) => {
          setVehicleSearch(value);
          setSelectedVehicle(null);
        }}
        onVehicleSelect={(vehicle) => {
          setSelectedVehicle(vehicle);
          setVehicleSearch("");
          setSearchResults([]);
          if (vehicle?.driver) {
            setDriverName(vehicle.driver);
          }
        }}
        onDriverNameChange={setDriverName}
        onMechanicNameChange={setMechanicName}
        onClose={() => setShowCreateModal(false)}
        onCreate={createInspection}
      />

      <DetailInspectionModal
        show={showDetailModal}
        isDarkMode={isDarkMode}
        selectedInspection={selectedInspection}
        savingResponse={savingResponse}
        onClose={() => setShowDetailModal(false)}
        onTabChange={(tabIndex) => {
          if (selectedInspection) {
            setSelectedInspection({
              ...selectedInspection,
              activeTab: tabIndex,
            });
          }
        }}
        onSaveResponse={saveItemResponse}
        onUpdateNotes={(sheetId, itemId, notes) => {
          // El debounce ya está en DetailInspectionModal, aquí solo guardamos
          const sheet = selectedInspection?.sheetDetails.find(
            (s) => s.id === sheetId
          );
          const item = sheet?.items.find((i) => i.id === itemId);
          if (item?.response) {
            saveItemResponse(sheetId, itemId, item.response, notes);
          }
        }}
      />

      <DateRangeReportModal
        show={showDateRangeModal}
        isDarkMode={isDarkMode}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onClose={() => setShowDateRangeModal(false)}
        onDownload={() => {
          if (startDate && endDate && selectedWorkshop) {
            setIsDownloadingReport(true);
            window.open(
              `${API_BASE_URL}/inspections/report/range?workshop_id=${selectedWorkshop.id}&start_date=${startDate}&end_date=${endDate}`,
              "_blank"
            );
            // Hide loading after 3 seconds (download should have started by then)
            setTimeout(() => {
              setIsDownloadingReport(false);
              setShowDateRangeModal(false);
            }, 3000);
          } else {
            showToast("Please select both start and end dates", "removed");
          }
        }}
      />

      <ConfirmCloseModal
        show={confirmCloseId !== null}
        isDarkMode={isDarkMode}
        vehicleNumber={
          gridItems.find((g) => g.inspection.id === confirmCloseId)?.inspection
            .vehicle_number || ""
        }
        isUpdating={updatingStatus}
        comment={closeComment}
        onCommentChange={setCloseComment}
        onClose={() => {
          setConfirmCloseId(null);
          setCloseComment("");
        }}
        onConfirm={async () => {
          const inspection = gridItems.find(
            (g) => g.inspection.id === confirmCloseId
          );
          if (inspection) {
            try {
              await toggleInspectionStatus(
                inspection.inspection.id,
                inspection.inspection.status,
                closeComment
              );
            } finally {
              // Always close modal regardless of success or error
              setConfirmCloseId(null);
              setCloseComment("");
            }
          }
        }}
      />

      {/* Report Preview Modal */}
      <ReportPreviewModal
        show={showReportPreview}
        isDarkMode={isDarkMode}
        inspectionId={selectedReportInspection?.id || 0}
        vehicleNumber={selectedReportInspection?.vehicleNumber || ""}
        reportUrl={
          selectedReportInspection
            ? `${API_BASE_URL}/inspections/${selectedReportInspection.id}/report/pdf`
            : ""
        }
        onClose={() => {
          setShowReportPreview(false);
          setSelectedReportInspection(null);
        }}
      />

      {/* Toast Notification */}
      <InspectionToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* Global Report Download Loading Overlay */}
      {isDownloadingReport && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div
            className={`text-center p-8 rounded-lg ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            } shadow-xl max-w-md mx-4`}
          >
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-16 w-16 text-[#DC5B23]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3
              className={`text-xl font-bold mb-3 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Generating Report
            </h3>
            <p
              className={`text-sm leading-relaxed ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              This may take up to 2 minutes depending on the size of the report.
              <br />
              <strong className="text-[#DC5B23]">Please be patient.</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectionBoard;
